import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import { getSentryReactRootOptions, initSentry } from '@/shared/lib/sentry'
import { registerCspViolationReporting } from '@/shared/lib/cspReporting'
import { dispatchSwNeedRefresh } from '@/features/sw/dispatchSwNeedRefresh'
import { setUpdateSW } from '@/features/sw/swUpdateStore'
import { initThemeFromBoot } from '@/shared/lib/themeStore'
import { applyTheme, resolveTheme } from '@/shared/lib/themeUtils'
import App from '@/app/App'
import { registerNativeOAuthDeepLinks } from '@/app/nativeDeepLinkBootstrap'
import { PosthogAppProvider } from '@/shared/lib/posthog/PosthogAppProvider'
import { branding } from '@/shared/config/branding'
import { fetchRemoteAppVersion } from '@/shared/pwa/remoteAppVersion'
import { isPublishedAppVersionNewer } from '@/shared/lib/appVersionCompare'
import { clearPwaCaches } from '@/shared/lib/clearPwaCaches'
import { purgeLocalAiVectorStoreOnBoot } from '@/shared/lib/clearUserData'
import { hideSplashScreen } from '@/shared/lib/platform/splashScreen'

const isNativeBuild = import.meta.env.VITE_TARGET_PLATFORM === 'native'

/** Se o React não pintar a tempo (erro cedo / bundle lento), esconde a splash nativa mesmo assim. */
const NATIVE_SPLASH_FAILSAFE_MS = 4000

const SYNAP_SILENT_BOOT_FLAG_PREFIX = 'synap_silent_boot_done_'

/**
 * Se `version.json` na origem for mais novo que o bundle em execução, limpa caches PWA e recarrega
 * antes do primeiro paint — reduz falhas de import dinâmico após deploy.
 */
async function trySilentReloadIfPublishedVersionIsNewer(): Promise<void> {
  if (import.meta.env.DEV || isNativeBuild) return
  try {
    const remote = await fetchRemoteAppVersion()
    if (!remote || !isPublishedAppVersionNewer(remote.version, __APP_VERSION__)) return
    const flag = `${SYNAP_SILENT_BOOT_FLAG_PREFIX}${remote.version}`
    if (sessionStorage.getItem(flag)) return
    sessionStorage.setItem(flag, '1')
    await clearPwaCaches()
    window.location.reload()
  } catch {
    /* offline / rede */
  }
}

// Aplica tema imediatamente (localStorage) para evitar flash. ThemeLoader aplica do servidor após login.

const initTheme = () => {
  try {
    initThemeFromBoot()
  } catch {
    applyTheme(resolveTheme('system'))
  }
}
initTheme()

const upsertMeta = (name: string, content: string) => {
  const selector = `meta[name="${name}"]`
  const existing = document.querySelector<HTMLMetaElement>(selector)
  if (existing) {
    existing.content = content
    return
  }

  const meta = document.createElement('meta')
  meta.name = name
  meta.content = content
  document.head.appendChild(meta)
}

const upsertLink = (rel: string, href: string) => {
  const selector = `link[rel="${rel}"]`
  const existing = document.querySelector<HTMLLinkElement>(selector)
  if (existing) {
    existing.href = href
    return
  }

  const link = document.createElement('link')
  link.rel = rel
  link.href = href
  document.head.appendChild(link)
}

const applyBrandingMetadata = () => {
  const v = __APP_VERSION__
  document.title = branding.appName
  upsertMeta('description', branding.description)
  // theme-color segue o tema da app (applyTheme → syncThemeColorMeta), não a cor da marca.
  upsertMeta('apple-mobile-web-app-title', branding.appName)
  upsertLink('icon', `${branding.iconPath}?v=${v}`)
  upsertLink('apple-touch-icon', `${branding.appleTouchIconPath}?v=${v}`)
}

applyBrandingMetadata()

/** 6 min (mantém a cadência de main) + checagem em focus/visibilitychange; evita `registration.update()` excessivo. */
const CHECK_INTERVAL_MS = 6 * 60 * 1000

let swUpdateIntervalId: ReturnType<typeof setInterval> | null = null
let swVisibilityHandler: (() => void) | null = null
let swFocusHandler: (() => void) | null = null

const registerPwaServiceWorker = (): void => {
  if (isNativeBuild) {
    setUpdateSW(async () => {})
    return
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      dispatchSwNeedRefresh()
    },
    onOfflineReady() {
      if (import.meta.env.DEV) {
        console.log('[SW] Pronto para uso offline.')
      }
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      if (swUpdateIntervalId !== null) {
        clearInterval(swUpdateIntervalId)
        swUpdateIntervalId = null
      }
      if (swVisibilityHandler) {
        document.removeEventListener('visibilitychange', swVisibilityHandler)
        swVisibilityHandler = null
      }
      if (swFocusHandler) {
        window.removeEventListener('focus', swFocusHandler)
        swFocusHandler = null
      }

      const checkForUpdate = () => {
        if (registration.installing || !navigator?.serviceWorker) return
        if ('connection' in navigator && !navigator.onLine) return
        void registration.update().catch(() => {
          /* SW em estado inválido após deploy/cache — ignorar ruído no console */
        })
      }

      checkForUpdate()
      swUpdateIntervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS)

      swVisibilityHandler = () => {
        if (document.visibilityState === 'visible') checkForUpdate()
      }
      document.addEventListener('visibilitychange', swVisibilityHandler)

      swFocusHandler = () => {
        checkForUpdate()
      }
      window.addEventListener('focus', swFocusHandler)
    },
  })
  setUpdateSW(updateSW)
}

const bootstrap = async () => {
  await initSentry()
  registerCspViolationReporting()
  void purgeLocalAiVectorStoreOnBoot()
  registerNativeOAuthDeepLinks()
  registerPwaServiceWorker()
  await trySilentReloadIfPublishedVersionIsNewer()
  const rootEl = document.getElementById('root')!
  const sentryRoot = getSentryReactRootOptions()
  const root = sentryRoot ? createRoot(rootEl, sentryRoot) : createRoot(rootEl)
  // Failsafe: se SplashScreen não chegar a esconder (erro no tree / stall), some a splash nativa.
  window.setTimeout(() => {
    void hideSplashScreen().catch(() => {})
  }, NATIVE_SPLASH_FAILSAFE_MS)
  root.render(
    <StrictMode>
      <PosthogAppProvider>
        <App />
      </PosthogAppProvider>
    </StrictMode>,
  )
}

void bootstrap()
